import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, Link2Off, CheckCircle2, Pencil, RefreshCw, Loader2 } from 'lucide-react';
import { adminSeoAuditAPI } from '../services/api';

const SECTIONS = [
  { key: 'isolated', title: 'Aucun lien voisin', help: 'Ni sous-catégorie voisine garnie, ni véhicule compatible : le bloc de maillage est vide. Recatégoriser la pièce ou ajouter ses compatibilités.' },
  { key: 'no_siblings', title: 'Sans sous-catégorie voisine', help: 'Seule sous-catégorie garnie de sa catégorie, ou pièce sans sous-catégorie : ajouter une catégorie secondaire ou garnir les sous-catégories voisines.' },
  { key: 'no_vehicles', title: 'Sans véhicule compatible', help: 'Aucune compatibilité véhicule : les liens « Pièces compatibles par véhicule » n’apparaissent pas.' },
];

const Row = ({ p, onEdit }) => (
  <li className="flex flex-wrap items-center gap-2 py-1.5 border-b border-gray-100 last:border-0 text-sm" data-testid={`orphan-product-${p.id}`}>
    <span className="font-medium text-gray-900 flex-1 min-w-[200px] leading-tight">{p.name}<span className="block text-xs text-gray-500 font-normal">{p.brand} {p.ref} · {p.category_name}{p.subcategory ? ` › ${p.subcategory}` : ''}{p.hint ? ` · ${p.hint}` : ''}</span></span>
    <a href={p.path} target="_blank" rel="noreferrer" className="text-xs text-gray-500 hover:text-orange-600 inline-flex items-center gap-1">Voir <ExternalLink size={11} /></a>
    <button type="button" onClick={() => onEdit?.(p.id)} className="text-xs text-orange-600 hover:underline inline-flex items-center gap-1" data-testid={`orphan-product-edit-${p.id}`}><Pencil size={12} /> Modifier</button>
  </li>
);

// Admin SEO card: products whose internal-linking block (sibling subcategories / vehicles) is empty
const OrphanLinksCard = ({ onEditProduct }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState('isolated');
  const [loading, setLoading] = useState(false);
  const load = () => { setLoading(true); adminSeoAuditAPI.orphanLinks().then(setData).catch(() => setData({ error: true })).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  if (!data || data.error) return null;
  const active = SECTIONS.find((s) => s.key === section);
  const warn = data.counts.isolated > 0;
  return (
    <div className={`rounded-xl shadow-sm p-5 mb-6 border ${warn ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100'}`} data-testid="orphan-links-card">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 text-left" data-testid="orphan-links-toggle">
        <div className="flex items-center gap-2">
          {warn ? <Link2Off className="text-amber-600" size={20} /> : <CheckCircle2 className="text-emerald-600" size={20} />}
          <div>
            <h2 className="font-semibold text-gray-900">Maillage interne des fiches produit {data.counts.isolated ? <span className="text-amber-700" data-testid="orphan-links-total">({data.counts.isolated} sans aucun lien)</span> : <span className="text-emerald-700">— OK</span>}</h2>
            <p className="text-xs text-gray-600">{data.counts.isolated} fiche(s) sans aucun lien voisin · {data.counts.no_siblings} sans sous-catégorie voisine · {data.counts.no_vehicles} sans véhicule compatible. Corrigez la catégorisation ou les compatibilités pour renforcer le maillage SEO.</p>
          </div>
        </div>
        {data.total > 0 && (open ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />)}
      </button>
      {open && data.total > 0 && (
        <div className="mt-4" data-testid="orphan-links-body">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {SECTIONS.map((s) => (
              <button key={s.key} type="button" onClick={() => setSection(s.key)} data-testid={`orphan-tab-${s.key}`} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${section === s.key ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-orange-400'}`}>
                {s.title} ({data.counts[s.key]})
              </button>
            ))}
            <button type="button" onClick={load} disabled={loading} className="ml-auto text-xs text-gray-500 hover:text-orange-600 inline-flex items-center gap-1" data-testid="orphan-links-refresh">{loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Actualiser</button>
          </div>
          <p className="text-xs text-gray-600 mb-2">{active.help}</p>
          <section className="bg-white rounded-lg border border-amber-100 p-3">
            {data[section].length === 0 ? <p className="text-xs text-gray-400">—</p> : (
              <ul className="max-h-96 overflow-y-auto">{data[section].map((p) => <Row key={p.id} p={p} onEdit={onEditProduct} />)}</ul>
            )}
            {data.counts[section] > data[section].length && <p className="text-xs text-gray-400 mt-2">{data[section].length} affichées sur {data.counts[section]}.</p>}
          </section>
        </div>
      )}
    </div>
  );
};

export default OrphanLinksCard;
